import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import VendorlyLogo from "@/components/VendorlyLogo";
import { CheckCircle, MapPin } from "@phosphor-icons/react";

export default function PublicForm() {
  const { slug } = useParams();
  const [vendor, setVendor] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [f, setF] = useState({ name: "", phone: "", email: "", preferences: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/public/vendor/${slug}`).then(r => setVendor(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    // Location is INHERITED from the vendor server-side — no dropdowns for customers.
    const payload = {
      name: f.name.trim(),
      phone: f.phone.trim(),
      email: f.email?.trim() || undefined,
      preferences: f.preferences?.trim() || undefined,
    };
    try {
      const { data } = await api.post(`/public/vendor/${slug}/customer`, payload);
      setDone(true);
      toast.success(data.message);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit");
    } finally { setBusy(false); }
  };

  if (notFound) return (
    <div className="min-h-screen bg-brand-bg flex items-center justify-center p-6" data-testid="public-notfound">
      <div className="bg-white border border-brand-border rounded-3xl p-10 text-center max-w-md">
        <h1 className="font-heading text-2xl font-bold">This link isn't active</h1>
        <p className="mt-2 text-brand-text/70">The vendor you're looking for was not found.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-brand-bg dot-bg" data-testid="public-form-page">
      <Toaster position="top-right" richColors />
      <div className="max-w-lg mx-auto px-6 py-12">
        <div className="flex items-center gap-2 mb-8">
          <VendorlyLogo size={26} />
          <span className="font-heading font-bold text-brand-text">Vendorly</span>
        </div>
        <div className="bg-white border border-brand-border rounded-3xl p-8 sm:p-10 shadow-[0_10px_40px_rgba(44,42,41,0.06)]">
          {done ? (
            <div className="text-center py-6" data-testid="public-success">
              <CheckCircle size={56} weight="duotone" className="text-brand-success mx-auto" />
              <h1 className="font-heading text-2xl font-bold mt-4">You're on the list.</h1>
              <p className="text-brand-text/70 mt-2">
                Thanks for joining <span className="font-semibold text-brand-text">{vendor?.business_name}</span>. Expect a friendly hello soon.
              </p>
            </div>
          ) : (
            <>
              <div className="text-xs uppercase tracking-[0.2em] text-brand-secondary mb-3">Join the list</div>
              <h1 className="font-heading font-bold text-3xl tracking-tight">
                Say hi to <span className="text-brand-primary">{vendor?.business_name || "…"}</span>
              </h1>
              {vendor?.city && vendor?.state && (
                <div className="mt-2 inline-flex items-center gap-1 text-xs text-brand-textMuted" data-testid="pf-vendor-location">
                  <MapPin size={12} weight="duotone" /> {vendor.city}, {vendor.state}
                </div>
              )}
              <p className="text-brand-text/70 mt-3">Drop your details to get updates, offers, and reminders on WhatsApp.</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <Field label="Your name" required value={f.name} onChange={(e)=>setF({...f, name: e.target.value})} testid="pf-name" />
                <Field label="Phone number" required value={f.phone} onChange={(e)=>setF({...f, phone: e.target.value})} testid="pf-phone" placeholder="+91 …" />
                <Field label="Email (optional)" type="email" value={f.email} onChange={(e)=>setF({...f, email: e.target.value})} testid="pf-email" />
                <Field label="What are you interested in? (optional)" value={f.preferences} onChange={(e)=>setF({...f, preferences: e.target.value})} testid="pf-pref" placeholder="Offers, new arrivals…" />
                <button type="submit" disabled={busy}
                  className="w-full h-12 rounded-full bg-brand-primary hover:bg-brand-primaryHover text-white font-semibold transition-colors disabled:opacity-60"
                  data-testid="pf-submit">
                  {busy ? "Sending…" : "Join the list"}
                </button>
                <p className="text-xs text-brand-textMuted text-center">
                  By joining, you agree to receive updates from {vendor?.business_name}. Your number is private.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, ...rest }) {
  return (
    <div>
      <label className="text-sm font-semibold">{label}</label>
      <input {...rest} data-testid={testid}
        className="mt-1.5 w-full h-12 px-4 rounded-xl bg-brand-bg border border-brand-border focus:border-brand-primary focus:ring-1 focus:ring-brand-primary outline-none transition-colors" />
    </div>
  );
}
